import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { or, eq } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import { Popover } from 'tio-ui/components/popover';
import { Dropdown } from 'tio-ui/components/collections';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import Component from '@glimmer/component';
import type PslfFormModel from 'tio-common/models/pslf-form';
import {
  PhPaperPlaneTilt,
  PhUserGear,
  PhEyeSlash,
  PhDotsThreeVertical,
  PhEye,
} from 'ember-phosphor-icons';

interface S {
  Args: {
    doFormAction?: (action: string, model: PslfFormModel) => void;
    isLoading: boolean;
    isResending?: boolean;
    model: PslfFormModel;
    onPreview: (model: PslfFormModel) => void;
    cellClass?: string;
  };
}

export default class PslfMyDocumentsMyFormsTableRowComponent extends Component<S> {
  handleMenuAction = (action: string) => {
    if (action === 'preview') {
      this.args.onPreview?.(this.args.model);
    } else {
      this.args.doFormAction?.(action, this.args.model);
    }
  };

  <template>
    <tr>
      {{#let @model.employerCompletedAt as |date|}}
        <td class={{@cellClass}} title={{date}}>
          {{#if date}}
            {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
          {{else if (or @model.isPrintAndMail @model.isPriorEmployerClosed)}}
            {{t "pslf.table.not_applicable"}}
          {{else if @model.isAwaitingSignature}}
            {{t "pslf.table.pending"}}
          {{/if}}
        </td>
      {{/let}}

      {{#let @model.borrowerCompletedAt as |date|}}
        <td class={{@cellClass}} title={{date}}>
          {{#if date}}
            {{safeFormatDate date month="short" day="2-digit" year="numeric"}}
          {{else}}
            {{t "pslf.table.pending"}}
          {{/if}}
        </td>
      {{/let}}

      <td class={{@cellClass}}>
        <Popover as |pop|>
          <span
            {{pop.trigger}}
            {{pop.anchor}}
            {{on "mouseenter" pop.open}}
            {{on "mouseleave" pop.close}}
          >
            {{t (concat "common.pslf.status." @model.status)}}
          </span>
          {{#if
            (or
              (eq @model.status "canceled")
              (eq @model.status "cancelled")
              (eq @model.status "submitted")
            )
          }}
            <pop.Content>
              <div class="p-4">
                {{t (concat "pslf.status_tippy." @model.status) htmlSafe=true}}
              </div>
            </pop.Content>
          {{/if}}
        </Popover>
      </td>
      <td class={{@cellClass}}>
        {{@model.approverDisplayEmail}}
      </td>
      <td class={{@cellClass}}>
        <div class="truncate">
          {{t (concat "common.pslf.form-type." @model.formType)}}
          <br />
          {{@model.employerDisplayName}}
        </div>
      </td>
      <td class={{@cellClass}}>
        <div class="flex justify-end">
          <Dropdown @placement="left" as |d|>
            <d.Trigger
              @intent="primary"
              @size="sm"
              @appearance="outlined"
              data-test-actions-trigger={{@model.id}}
            >
              <PhDotsThreeVertical @size="24" />
            </d.Trigger>
            <d.Menu
              class="max-w-full"
              @intent="primary"
              @onAction={{this.handleMenuAction}}
              as |Item|
            >
              {{#if @model.hasPdfPreview}}
                <Item @key="preview" data-test-preview={{@model.id}}>
                  <span class="flex items-center gap-2">
                    <PhEye @size="16" />
                    {{t "pslf.document_repo.preview"}}
                  </span>
                </Item>
              {{/if}}
              {{#if @model.canResendEmail}}
                <Item @key="resend" data-test-resend={{@model.id}}>
                  <span class="flex items-center gap-2">
                    <PhPaperPlaneTilt @size="16" />
                    {{t "pslf.document_repo.resend"}}
                  </span>
                </Item>
              {{/if}}
              {{#if @model.canChangeApprover}}
                <Item @key="changeApprover" data-test-change-approver={{@model.id}}>
                  <span class="flex items-center gap-2">
                    <PhUserGear @size="16" />
                    {{t "pslf.document_repo.change_approver"}}
                  </span>
                </Item>
              {{/if}}
              {{#if @model.canCancel}}
                <Item
                  @key="cancel"
                  @intent="danger"
                  @class="text-danger"
                  data-test-cancel={{@model.id}}
                >
                  <span class="flex items-center gap-2">
                    <PhEyeSlash @size="16" />
                    {{t "disable"}}
                  </span>
                </Item>
              {{/if}}
            </d.Menu>
          </Dropdown>
        </div>
      </td>
    </tr>
  </template>
}
